import React, { useState } from "react";
import RecordPage from "../../components/RecordPage";
import Modal from "../../components/Modal";
import Select from "../../components/Select";
import EntityFiles, {
  attachmentsField,
  mainFromAttachments,
  afterSaveFor,
} from "../../components/EntityFiles";
import api from "../../core/api";
import { useApp } from "../../core/AppContext";
import { formatDate, formatLongDate, todayIso } from "../../core/format";

const EMPTY = { persons: [], vehicles: [], vessels: [], equipment: [] };
const mk = (obj) =>
  Object.entries(obj || {}).map(([value, label]) => ({ value, label }));
const parseJ = (v, fb) => {
  if (!v) return fb;
  if (typeof v === "object") return v;
  try {
    return JSON.parse(v);
  } catch {
    return fb;
  }
};
const esc = (s) =>
  String(s ?? "").replace(
    /[&<>"]/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c],
  );

/* ---------- فیلدهای کوچک ---------- */
const T = ({ label, value, onSet, ltr, full, ...p }) => (
  <div className="field" style={full ? { gridColumn: "1 / -1" } : undefined}>
    <label>{label}</label>
    <input
      className={ltr ? "ltr" : ""}
      value={value || ""}
      onChange={(e) => onSet(e.target.value)}
      {...p}
    />
  </div>
);
const S = ({ label, value, onSet, options }) => (
  <div className="field">
    <label>{label}</label>
    <Select value={value || ""} onChange={onSet} options={options} />
  </div>
);

/** یک بخش با دکمه‌ی افزودن و کارت‌های تکی */
function Section({ icon, title, items, blank, onChange, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div className="box-head">
        <h3 style={{ fontSize: 14 }}>
          <i className={icon} /> {title}
        </h3>
        <button
          type="button"
          className="btn btn-sm btn-ghost"
          onClick={() => onChange([...items, { ...blank }])}
        >
          <i className="fa-solid fa-plus" /> افزودن
        </button>
      </div>
      {items.length === 0 && <div className="hint">موردی اضافه نشده است.</div>}
      {items.map((it, i) => (
        <div
          key={i}
          style={{
            border: "1.5px dashed var(--border)",
            borderRadius: 12,
            padding: "12px 12px 0",
            marginBottom: 10,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 8,
            }}
          >
            <b style={{ fontSize: 13 }}>مورد {i + 1}</b>
            <button
              type="button"
              className="icon-btn danger"
              title="حذف"
              onClick={() => onChange(items.filter((_, j) => j !== i))}
            >
              <i className="fa-solid fa-xmark" />
            </button>
          </div>
          <div className="grid2">
            {children(it, (k, v) =>
              onChange(items.map((x, j) => (j === i ? { ...x, [k]: v } : x))),
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function DetailsEditor({ value, onChange, meta }) {
  const d = { ...EMPTY, ...(value || {}) };
  const up = (k) => (list) => onChange({ ...d, [k]: list });
  const injury = mk(meta?.injuryLevels),
    damage = mk(meta?.damageLevels);

  return (
    <div style={{ gridColumn: "1 / -1" }}>
      <div className="alert info">
        افراد، خودروها، شناورها و تجهیزات آسیب‌دیده را یکی‌یکی اضافه کنید (در
        صورت نبودن، خالی بگذارید).
      </div>

      <Section
        icon="fa-solid fa-user-injured"
        title="افراد آسیب‌دیده"
        items={d.persons}
        onChange={up("persons")}
        blank={{
          firstName: "",
          lastName: "",
          position: "",
          injury: "",
          note: "",
        }}
      >
        {(it, set) => (
          <>
            <T
              label="نام"
              value={it.firstName}
              onSet={(v) => set("firstName", v)}
            />
            <T
              label="نام خانوادگی"
              value={it.lastName}
              onSet={(v) => set("lastName", v)}
            />
            <T
              label="سمت"
              value={it.position}
              onSet={(v) => set("position", v)}
            />
            <S
              label="نوع آسیب"
              value={it.injury}
              onSet={(v) => set("injury", v)}
              options={injury}
            />
            {it.injury === "other" && (
              <T
                full
                label="توضیحات آسیب (الزامی)"
                value={it.note}
                onSet={(v) => set("note", v)}
              />
            )}
          </>
        )}
      </Section>

      <Section
        icon="fa-solid fa-car-burst"
        title="خودروهای آسیب‌دیده"
        items={d.vehicles}
        onChange={up("vehicles")}
        blank={{
          type: "",
          typeOther: "",
          plate: "",
          driverName: "",
          driverPhone: "",
          damage: "",
          note: "",
        }}
      >
        {(it, set) => (
          <>
            <S
              label="نوع خودرو"
              value={it.type}
              onSet={(v) => set("type", v)}
              options={mk(meta?.vehicleTypes)}
            />
            {it.type === "other" ? (
              <T
                label="عنوان نوع خودرو"
                value={it.typeOther}
                onSet={(v) => set("typeOther", v)}
              />
            ) : (
              <T
                label="شماره پلاک"
                ltr
                value={it.plate}
                onSet={(v) => set("plate", v)}
              />
            )}
            {it.type === "other" && (
              <T
                label="شماره پلاک"
                ltr
                value={it.plate}
                onSet={(v) => set("plate", v)}
              />
            )}
            <T
              label="نام و نام خانوادگی راننده"
              value={it.driverName}
              onSet={(v) => set("driverName", v)}
            />
            <T
              label="شماره تماس راننده"
              ltr
              inputMode="numeric"
              maxLength={11}
              value={it.driverPhone}
              onSet={(v) => set("driverPhone", v)}
            />
            <S
              label="نوع خسارت"
              value={it.damage}
              onSet={(v) => set("damage", v)}
              options={damage}
            />
            <T
              full
              label="توضیحات"
              value={it.note}
              onSet={(v) => set("note", v)}
            />
          </>
        )}
      </Section>

      <Section
        icon="fa-solid fa-ship"
        title="شناورهای آسیب‌دیده"
        items={d.vessels}
        onChange={up("vessels")}
        blank={{
          type: "",
          typeOther: "",
          name: "",
          captainName: "",
          captainPhone: "",
          damage: "",
          note: "",
        }}
      >
        {(it, set) => (
          <>
            <S
              label="نوع شناور"
              value={it.type}
              onSet={(v) => set("type", v)}
              options={mk(meta?.vesselTypes)}
            />
            {it.type === "other" && (
              <T
                label="عنوان نوع شناور"
                value={it.typeOther}
                onSet={(v) => set("typeOther", v)}
              />
            )}
            <T
              label="نام شناور"
              value={it.name}
              onSet={(v) => set("name", v)}
            />
            <T
              label="نام فرمانده شناور"
              value={it.captainName}
              onSet={(v) => set("captainName", v)}
            />
            <T
              label="شماره تماس فرمانده"
              ltr
              inputMode="numeric"
              maxLength={11}
              value={it.captainPhone}
              onSet={(v) => set("captainPhone", v)}
            />
            <S
              label="نوع خسارت"
              value={it.damage}
              onSet={(v) => set("damage", v)}
              options={damage}
            />
            <T
              full
              label="توضیحات"
              value={it.note}
              onSet={(v) => set("note", v)}
            />
          </>
        )}
      </Section>

      <Section
        icon="fa-solid fa-screwdriver-wrench"
        title="تجهیزات آسیب‌دیده"
        items={d.equipment}
        onChange={up("equipment")}
        blank={{ name: "", damage: "", note: "" }}
      >
        {(it, set) => (
          <>
            <T
              label="نام تجهیزات"
              value={it.name}
              onSet={(v) => set("name", v)}
            />
            <S
              label="نوع خسارت"
              value={it.damage}
              onSet={(v) => set("damage", v)}
              options={damage}
            />
            <T
              full
              label="توضیحات"
              value={it.note}
              onSet={(v) => set("note", v)}
            />
          </>
        )}
      </Section>
    </div>
  );
}

/* ---------- نمای گزارش و چاپ ---------- */
function buildSections(d, meta) {
  const lv = (m, k) => meta?.[m]?.[k] || k;
  const typ = (map, x) =>
    x.type === "other" ? x.typeOther || "سایر" : lv(map, x.type);
  const dmg = (x) =>
    lv("damageLevels", x.damage) + (x.note ? ` — ${x.note}` : "");
  return [
    {
      title: "افراد آسیب‌دیده",
      head: ["نام و نام خانوادگی", "سمت", "نوع آسیب"],
      rows: (d.persons || []).map((p) => [
        `${p.firstName} ${p.lastName}`,
        p.position || "—",
        lv("injuryLevels", p.injury) + (p.note ? ` — ${p.note}` : ""),
      ]),
    },
    {
      title: "خودروهای آسیب‌دیده",
      head: ["نوع خودرو", "پلاک", "راننده", "تماس", "خسارت"],
      rows: (d.vehicles || []).map((x) => [
        typ("vehicleTypes", x),
        x.plate || "—",
        x.driverName || "—",
        x.driverPhone || "—",
        dmg(x),
      ]),
    },
    {
      title: "شناورهای آسیب‌دیده",
      head: ["نوع شناور", "نام شناور", "فرمانده", "تماس", "خسارت"],
      rows: (d.vessels || []).map((x) => [
        typ("vesselTypes", x),
        x.name,
        x.captainName || "—",
        x.captainPhone || "—",
        dmg(x),
      ]),
    },
    {
      title: "تجهیزات آسیب‌دیده",
      head: ["نام تجهیزات", "خسارت"],
      rows: (d.equipment || []).map((x) => [x.name, dmg(x)]),
    },
  ].filter((s) => s.rows.length);
}

const kindText = (r, meta) =>
  r.kind === "other" && r.kindOther
    ? `سایر: ${r.kindOther}`
    : meta?.incidentKinds?.[r.kind] || r.kind;

function printReport(r, meta) {
  const w = window.open("", "_blank");
  if (!w) return;
  const d = parseJ(r.details, EMPTY);
  const crew = parseJ(r.crew, []);
  const tbl = (s) =>
    `<h4>${esc(s.title)}</h4><table><tr>${s.head.map((h) => `<th>${esc(h)}</th>`).join("")}</tr>${s.rows.map((x) => `<tr>${x.map((c) => `<td>${esc(c)}</td>`).join("")}</tr>`).join("")}</table>`;
  const block = (t, v) =>
    v ? `<h4>${t}</h4><p>${esc(v).replace(/\n/g, "<br>")}</p>` : "";
  w.document
    .write(`<!doctype html><html lang="fa" dir="rtl"><head><meta charset="utf-8"><title>گزارش حادثه</title>
<style>body{font-family:Tahoma,Vazirmatn,sans-serif;font-size:13px;margin:24px;line-height:2}h2{text-align:center}h4{margin:14px 0 4px;color:#0D3B66}
table{width:100%;border-collapse:collapse}th,td{border:1px solid #999;padding:4px 8px;text-align:right}th{background:#eef}
.sign{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-top:20px}.sign div{border:1px solid #999;min-height:70px;padding:6px}</style></head><body>
<h2>فرم گزارش حادثه در بندر تجاری و مسافری چارک</h2>
<p>تاریخ وقوع: <b>${esc(formatDate(r.incidentDate))}</b> &nbsp; ساعت: <b>${esc(r.incidentTime || "—")}</b> &nbsp; محل: <b>${esc(r.location)}</b></p>
<p>نوع حادثه: <b>${esc(kindText(r, meta))}</b></p>
${block("شرح حادثه", r.description)}${buildSections(d, meta).map(tbl).join("")}
${block("شرح خسارت‌های مالی", r.financialDamage)}${block("شرح خسارت‌های جانی", r.casualtyDescription)}${block("اقدامات انجام‌شده", r.actions)}
<h4>ثبت‌کننده و هم‌شیفتی‌ها (شیفت ایمنی و آتش‌نشانی بندر)</h4>
<div class="sign">${crew.map((c) => `<div><b>${esc(c.name)}</b>${c.positionName ? ` — ${esc(c.positionName)}` : ""}${c.submitter ? " (ثبت‌کننده)" : ""}<br>امضاء:</div>`).join("")}</div>
</body></html>`);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 300);
}

function ReportModal({ row, meta, onClose }) {
  const d = parseJ(row.details, EMPTY);
  const crew = parseJ(row.crew, []);
  const sections = buildSections(d, meta);
  const Block = ({ t, v }) =>
    v ? (
      <div className="field">
        <label>{t}</label>
        <div style={{ whiteSpace: "pre-wrap", lineHeight: 2 }}>{v}</div>
      </div>
    ) : null;
  return (
    <Modal
      wide
      title="گزارش حادثه"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={() => printReport(row, meta)}>
            <i className="fa-solid fa-print" /> چاپ
          </button>
          <button className="btn btn-ghost" onClick={onClose}>
            بستن
          </button>
        </>
      }
    >
      <div style={{ marginBottom: 12, lineHeight: 2 }}>
        <b>{kindText(row, meta)}</b>
        <div className="hint">
          {formatLongDate(row.incidentDate)}{" "}
          {row.incidentTime ? `· ساعت ${row.incidentTime}` : ""} ·{" "}
          {row.location}
        </div>
      </div>
      <Block t="شرح حادثه" v={row.description} />
      {row.involved && (
        <Block
          t="افراد / شناورها / خودروهای درگیر (ثبت قدیمی)"
          v={row.involved}
        />
      )}
      {sections.map((s) => (
        <div key={s.title} className="field">
          <label>{s.title}</label>
          <div className="table-wrap">
            <table className="tbl" style={{ minWidth: 420 }}>
              <thead>
                <tr>
                  {s.head.map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {s.rows.map((x, i) => (
                  <tr key={i}>
                    {x.map((c, j) => (
                      <td key={j}>{c}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      <Block t="شرح خسارت‌های مالی" v={row.financialDamage} />
      {row.damage && <Block t="خسارت و اقدامات (ثبت قدیمی)" v={row.damage} />}
      <Block t="شرح خسارت‌های جانی" v={row.casualtyDescription} />
      <Block t="اقدامات انجام‌شده" v={row.actions} />
      {crew.length > 0 && (
        <div className="field">
          <label>ثبت‌کننده و هم‌شیفتی‌ها</label>
          <div className="chips">
            {crew.map((c, i) => (
              <span className="chip" key={i}>
                {c.name}
                {c.positionName ? ` — ${c.positionName}` : ""}
                {c.submitter ? " (ثبت‌کننده)" : ""}
              </span>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
}

const incidentPrint = (r, meta) => {
  const d = parseJ(r.details, EMPTY);
  const crew = parseJ(r.crew, []);
  return {
    title: "فرم گزارش حادثه در بندر تجاری و مسافری چارک",
    docNo: `INC-${String(r.id).padStart(4, "0")}`,
    meta: [
      ["نوع حادثه", kindText(r, meta)],
      ["تاریخ وقوع", formatDate(r.incidentDate)],
      ["ساعت", r.incidentTime || "—"],
      ["محل", r.location],
      ["ثبت‌کننده", r.creatorName],
    ],
    blocks: [
      ["شرح حادثه", r.description],
      ["افراد / شناورها / خودروهای درگیر (ثبت قدیمی)", r.involved],
      ["شرح خسارت‌های مالی", r.financialDamage],
      ["خسارت و اقدامات (ثبت قدیمی)", r.damage],
      ["شرح خسارت‌های جانی", r.casualtyDescription],
      ["اقدامات انجام‌شده", r.actions],
    ],
    tables: buildSections(d, meta),
    signatures: crew.map(
      (c) =>
        `${c.name}${c.positionName ? ` — ${c.positionName}` : ""}${c.submitter ? " (ثبت‌کننده)" : ""}`,
    ),
    // فایل اصلی + همه‌ی فایل‌های چندگانه‌ی حادثه
    load: async () => {
      const files = [];
      if (r.hasFile)
        files.push({
          path: `/incidents/${r.id}/file`,
          name: "پیوست اصلی حادثه",
          optional: false,
        });
      try {
        const { data } = await api.get(
          `/equipment-files/incident/${r.id}/files`,
        );
        data.items.forEach((x) =>
          files.push({
            path: `/equipment-files/incident/${r.id}/files/${x.id}`,
            name: x.name,
            optional: false,
          }),
        );
      } catch {}
      return { files };
    },
  };
};

/* ---------- صفحه ---------- */
export default function Incidents() {
  const { user, meta } = useApp();
  const admin = user.role === "admin";
  const staff = user.role === "staff";

  const [report, setReport] = useState(null);
  const [filesFor, setFilesFor] = useState(null);
  const [tick, setTick] = useState(0);
  const kinds = mk(meta?.incidentKinds);

  return (
    <>
      <RecordPage
        key={tick}
        printReport={incidentPrint}
        title="ثبت حوادث"
        endpoint="/incidents"
        multipart
        createLabel="ثبت حادثه"
        canCreate={admin || staff}
        canEdit={admin}
        canDelete={admin}
        subtitle={admin ? "همه‌ی حوادث ثبت‌شده" : "حوادثی که شما ثبت کرده‌اید"}
        initial={{ incidentDate: todayIso(), details: EMPTY }}
        filters={[{ name: "kind", label: "نوع حادثه", options: kinds }]}
        toForm={(r) => ({
          ...r,
          kindOther: r.kindOther || "",
          incidentTime: r.incidentTime || "",
          details: parseJ(r.details, EMPTY),
        })}
        hideFileButton
        transformBody={(fd, form) => {
          fd.append("details", JSON.stringify(form.details || EMPTY));
          mainFromAttachments(fd, form);
        }}
        afterSave={afterSaveFor("incident")}
        rowExtra={(r) => (
          <>
            <button
              className="icon-btn"
              title="فایل‌ها و تصاویر"
              onClick={() => setFilesFor(r)}
            >
              <i className="fa-solid fa-folder-open" />
            </button>
            <button
              className="icon-btn"
              title="مشاهده و چاپ گزارش"
              onClick={() => setReport(r)}
            >
              <i className="fa-regular fa-file-lines" />
            </button>
          </>
        )}
        columns={[
          {
            key: "incidentDate",
            label: "تاریخ",
            render: (r) => (
              <span>
                {formatDate(r.incidentDate)}{" "}
                {r.incidentTime && <small dir="ltr">{r.incidentTime}</small>}
              </span>
            ),
          },
          {
            key: "kind",
            label: "نوع حادثه",
            render: (r) => (
              <span className={`badge ${r.kind === "fire" ? "bad" : "wait"}`}>
                {kindText(r, meta)}
              </span>
            ),
          },
          { key: "location", label: "محل" },
          {
            key: "description",
            label: "شرح",
            render: (r) => (
              <div className="clip" title={r.description}>
                {r.description}
              </div>
            ),
          },
          ...(admin ? [{ key: "creatorName", label: "ثبت‌کننده" }] : []),
        ]}
        getFields={(form) => [
          {
            name: "incidentDate",
            label: "تاریخ وقوع حادثه",
            type: "date",
            required: true,
          },
          {
            name: "incidentTime",
            label: "ساعت وقوع حادثه",
            type: "time",
            required: true,
          },
          {
            name: "location",
            label: "محل دقیق وقوع حادثه",
            required: true,
            full: true,
          },
          {
            name: "kind",
            label: "نوع حادثه",
            type: "select",
            options: kinds,
            required: true,
            full: form.kind !== "other",
          },
          {
            name: "kindOther",
            label: "توضیحات نوع حادثه",
            required: true,
            hidden: form.kind !== "other",
          },
          {
            name: "description",
            label: "شرح حادثه",
            type: "textarea",
            required: true,
          },
          {
            name: "details",
            label: "آسیب‌دیدگان",
            type: "custom",
            noBody: true,
            full: true,
            render: ({ form: f, setForm }) => (
              <DetailsEditor
                meta={meta}
                value={f.details}
                onChange={(v) => setForm((s) => ({ ...s, details: v }))}
              />
            ),
          },
          {
            name: "financialDamage",
            label: "شرح خسارت‌های مالی",
            type: "textarea",
          },
          {
            name: "casualtyDescription",
            label: "شرح خسارت‌های جانی",
            type: "textarea",
          },
          {
            name: "actions",
            label:
              "اقدامات انجام‌شده توسط واحد ایمنی و آتش‌نشانی و سایر ارگان‌ها",
            type: "textarea",
          },
          attachmentsField(
            "تصاویر و فایل‌های پیوست (اختیاری، چند فایل مجاز است)",
          ),
        ]}
      />

      {report && (
        <ReportModal row={report} meta={meta} onClose={() => setReport(null)} />
      )}

      {filesFor && (
        <EntityFiles
          type="incident"
          id={filesFor.id}
          title={`${kindText(filesFor, meta)} — ${filesFor.location}`}
          mainPath={filesFor.hasFile ? `/incidents/${filesFor.id}/file` : null}
          canAdd={admin || staff}
          canRemove={admin}
          onClose={() => setFilesFor(null)}
          onChanged={() => setTick((t) => t + 1)}
        />
      )}
    </>
  );
}
